import axios from 'axios';
import type { IntegrationJson } from './model/Integration';
import Integration from './model/Integration';
import type { ResourceCollection } from '@/api/model/ResourceCollection';
import type { StoreIntegrationRequest } from '@/api/types/Integrations';
import type { Resource } from '@/api/model/Resource';
import type { Organisation } from '@/api/model/Organisation';
import { guardId } from '@/api/guardId';

function filterObject(obj: { [key: string]: unknown } | object, keys: string[]): { [key: string]: unknown } {
    return Object.fromEntries(Object.entries(obj).filter(([key]) => keys.includes(key)));
}

export default function useIntegrationService() {
    return {
        async getAll(org: Organisation): Promise<Integration[]> {
            guardId(org.ulid);
            const response = await axios.get<ResourceCollection<IntegrationJson>>(
                `/api/organisations/${org.ulid}/report-sources`,
            );
            return response.data.data.map((integration) => Integration.fromJSON(integration));
        },

        async create(org: Organisation, data: StoreIntegrationRequest): Promise<Integration> {
            guardId(org.ulid);
            const response = await axios.post<Resource<IntegrationJson>>(
                `/api/organisations/${org.ulid}/report-sources`,
                data,
            );
            return Integration.fromJSON(response.data.data);
        },

        async update(organisation: Organisation, integration: Integration): Promise<Integration> {
            guardId(organisation.ulid, integration.uuid);
            const data = filterObject(integration, [
                'name',
                'type',
                'auth_token',
                'client_key',
                'client_secret',
                'has_daily_sync',
                'track_problem_orders',
                'region_code',
            ]);
            const response = await axios.put<Resource<IntegrationJson>>(
                `/api/organisations/${organisation.ulid}/report-sources/${integration.uuid}`,
                data,
            );
            return Integration.fromJSON(response.data.data);
        },

        async delete(org: Organisation, uuid: string): Promise<void> {
            guardId(org.ulid, uuid);
            await axios.delete(`/api/organisations/${org.ulid}/report-sources/${uuid}`);
        },

        async configureWebhooks(org: Organisation, integration: Integration | string): Promise<Integration> {
            const uuid: string = integration instanceof Integration ? (integration.uuid as string) : integration;
            guardId(org.ulid, uuid);
            const response = await axios.post<Resource<IntegrationJson>>(
                `/api/organisations/${org.ulid}/report-sources/${uuid}/configure-webhooks`,
            );
            return Integration.fromJSON(response.data.data);
        },
    };
}
